import React from "react";
import { useTranslation } from "react-i18next";
import { Header } from "../components/shared";
import { CheckoutFooter } from "../components/checkout";
import "../styles/legal/LegalPages.css";

const TermsAndConditions = () => {
  const { t } = useTranslation("legal");

  const renderList = (items) => {
    if (!items || !Array.isArray(items)) return null;
    return (
      <ul>
        {items.map((item, index) => (
          <li key={index}>{item}</li>
        ))}
      </ul>
    );
  };

  return (
    <div className="privacy-policy-page">
      <Header />
      <div className="privacy-policy-container">
        <div className="privacy-policy-content">
          <h1 className="privacy-policy-title">{t("termsAndConditions.title")}</h1>
          <p className="privacy-policy-last-updated">{t("termsAndConditions.lastUpdated")}</p>

          {/* Section 1: Definition */}
          <section className="privacy-section">
            <h2>{t("termsAndConditions.section1.title")}</h2>
            <p>{t("termsAndConditions.section1.content")}</p>
          </section>

          {/* Section 2: Booking Requirements */}
          <section className="privacy-section">
            <h2>{t("termsAndConditions.section2.title")}</h2>

            <h3>{t("termsAndConditions.section2.driverRequirements.title")}</h3>
            <p>{t("termsAndConditions.section2.driverRequirements.content")}</p>

            <h3>{t("termsAndConditions.section2.ageRequirements.title")}</h3>
            {renderList(t("termsAndConditions.section2.ageRequirements.items", { returnObjects: true }))}

            <h3>{t("termsAndConditions.section2.additionalDrivers.title")}</h3>
            <p>{t("termsAndConditions.section2.additionalDrivers.content")}</p>

            <h3>{t("termsAndConditions.section2.transmission.title")}</h3>
            <p>{t("termsAndConditions.section2.transmission.content")}</p>

            <h3>{t("termsAndConditions.section2.rateChanges.title")}</h3>
            <p>{t("termsAndConditions.section2.rateChanges.content")}</p>
          </section>

          {/* Section 3: Payment Terms */}
          <section className="privacy-section">
            <h2>{t("termsAndConditions.section3.title")}</h2>

            <h3>{t("termsAndConditions.section3.bookingPayment.title")}</h3>
            <p>{t("termsAndConditions.section3.bookingPayment.content")}</p>
            <p>{t("termsAndConditions.section3.bookingPayment.methods")}</p>

            <h3>{t("termsAndConditions.section3.balancePayment.title")}</h3>
            <p>{t("termsAndConditions.section3.balancePayment.content")}</p>
          </section>

          {/* Section 4: Security Deposit */}
          <section className="privacy-section">
            <h2>{t("termsAndConditions.section4.title")}</h2>

            <h3>{t("termsAndConditions.section4.depositRequirement.title")}</h3>
            <p>{t("termsAndConditions.section4.depositRequirement.content")}</p>

            <h3>{t("termsAndConditions.section4.failureToProvide.title")}</h3>
            <p>{t("termsAndConditions.section4.failureToProvide.content")}</p>

            <h3>{t("termsAndConditions.section4.depositReturn.title")}</h3>
            <p>{t("termsAndConditions.section4.depositReturn.description")}</p>
            {renderList(t("termsAndConditions.section4.depositReturn.items", { returnObjects: true }))}

            <h3>{t("termsAndConditions.section4.breachOfTerms.title")}</h3>
            <p>{t("termsAndConditions.section4.breachOfTerms.content")}</p>

            <h3>{t("termsAndConditions.section4.insufficientDeposit.title")}</h3>
            <p>{t("termsAndConditions.section4.insufficientDeposit.content")}</p>

            <h3>{t("termsAndConditions.section4.protectionPackages.title")}</h3>
            <p>{t("termsAndConditions.section4.protectionPackages.description")}</p>
            <table className="fees-table">
              <thead>
                <tr>
                  <th>{t("termsAndConditions.section4.protectionPackages.tableHeaders.package")}</th>
                  <th>{t("termsAndConditions.section4.protectionPackages.tableHeaders.deposit")}</th>
                </tr>
              </thead>
              <tbody>
                {["basic", "premium", "ultimate"].map((tier) => (
                  <tr key={tier}>
                    <td>{t(`termsAndConditions.section4.protectionPackages.packages.${tier}.name`)}</td>
                    <td>{t(`termsAndConditions.section4.protectionPackages.packages.${tier}.deposit`)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          {/* Section 5: Cancellations */}
          <section className="privacy-section">
            <h2>{t("termsAndConditions.section5.title")}</h2>
            <p>{t("termsAndConditions.section5.intro")}</p>

            <h3>{t("termsAndConditions.section5.rescheduling.title")}</h3>
            <p>{t("termsAndConditions.section5.rescheduling.content")}</p>
            <p>{t("termsAndConditions.section5.rescheduling.voucherInfo")}</p>
            {renderList(t("termsAndConditions.section5.rescheduling.voucherNotes", { returnObjects: true }))}

            <h3>{t("termsAndConditions.section5.cancellationFees.title")}</h3>
            <p>{t("termsAndConditions.section5.cancellationFees.description")}</p>
            <table className="fees-table">
              <thead>
                <tr>
                  <th>Cancellation Timing</th>
                  <th>Refund/Fee</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{t("termsAndConditions.section5.cancellationFees.fees.twoWeeksOrMore.timing")}</td>
                  <td>{t("termsAndConditions.section5.cancellationFees.fees.twoWeeksOrMore.fee")}</td>
                </tr>
                <tr>
                  <td>{t("termsAndConditions.section5.cancellationFees.fees.oneWeek.timing")}</td>
                  <td>{t("termsAndConditions.section5.cancellationFees.fees.oneWeek.fee")}</td>
                </tr>
                <tr>
                  <td>{t("termsAndConditions.section5.cancellationFees.fees.lessThanSixDays.timing")}</td>
                  <td>{t("termsAndConditions.section5.cancellationFees.fees.lessThanSixDays.fee")}</td>
                </tr>
              </tbody>
            </table>

            <h3>{t("termsAndConditions.section5.dateChanges.title")}</h3>
            <p>{t("termsAndConditions.section5.dateChanges.content")}</p>

            <h3>{t("termsAndConditions.section5.communicationOfChanges.title")}</h3>
            <p>{t("termsAndConditions.section5.communicationOfChanges.content")}</p>

            <h3>{t("termsAndConditions.section5.modificationFees.title")}</h3>
            {renderList(t("termsAndConditions.section5.modificationFees.items", { returnObjects: true }))}
          </section>

          {/* Section 6: Vehicle Delivery */}
          <section className="privacy-section">
            <h2>{t("termsAndConditions.section6.title")}</h2>

            <h3>{t("termsAndConditions.section6.atDeliveryCompany.title")}</h3>
            {renderList(t("termsAndConditions.section6.atDeliveryCompany.items", { returnObjects: true }))}

            <h3>{t("termsAndConditions.section6.atDeliveryRenter.title")}</h3>
            {renderList(t("termsAndConditions.section6.atDeliveryRenter.items", { returnObjects: true }))}

            <h3>{t("termsAndConditions.section6.timeSlots.title")}</h3>
            <p>{t("termsAndConditions.section6.timeSlots.content")}</p>
            {renderList(t("termsAndConditions.section6.timeSlots.items", { returnObjects: true }))}
          </section>

          {/* Section 7: Vehicle Return */}
          <section className="privacy-section">
            <h2>{t("termsAndConditions.section7.title")}</h2>

            <h3>{t("termsAndConditions.section7.returnConditions.title")}</h3>
            <p>{t("termsAndConditions.section7.returnConditions.content")}</p>
            {renderList(t("termsAndConditions.section7.returnConditions.items", { returnObjects: true }))}

            <h3>{t("termsAndConditions.section7.cleaningRequirements.title")}</h3>
            <p>{t("termsAndConditions.section7.cleaningRequirements.content")}</p>

            <h3>{t("termsAndConditions.section7.cleaningFees.title")}</h3>
            <p>{t("termsAndConditions.section7.cleaningFees.description")}</p>
            <table className="fees-table">
              <thead>
                <tr>
                  <th>Cleaning Issue</th>
                  <th>Fee</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{t("termsAndConditions.section7.cleaningFees.fees.dirty.issue")}</td>
                  <td>{t("termsAndConditions.section7.cleaningFees.fees.dirty.fee")}</td>
                </tr>
                <tr>
                  <td>{t("termsAndConditions.section7.cleaningFees.fees.largeModels.issue")}</td>
                  <td>{t("termsAndConditions.section7.cleaningFees.fees.largeModels.fee")}</td>
                </tr>
                <tr>
                  <td>{t("termsAndConditions.section7.cleaningFees.fees.toilet.issue")}</td>
                  <td>{t("termsAndConditions.section7.cleaningFees.fees.toilet.fee")}</td>
                </tr>
                <tr>
                  <td>{t("termsAndConditions.section7.cleaningFees.fees.smoking.issue")}</td>
                  <td>{t("termsAndConditions.section7.cleaningFees.fees.smoking.fee")}</td>
                </tr>
              </tbody>
            </table>

            <h3>{t("termsAndConditions.section7.lateReturn.title")}</h3>
            <p>{t("termsAndConditions.section7.lateReturn.content")}</p>

            <h3>{t("termsAndConditions.section7.fuelReturn.title")}</h3>
            <p>{t("termsAndConditions.section7.fuelReturn.content")}</p>

            <h3>{t("termsAndConditions.section7.additionalEquipment.title")}</h3>
            <p>{t("termsAndConditions.section7.additionalEquipment.content")}</p>
            <p>{t("termsAndConditions.section7.additionalEquipment.missingDamaged")}</p>

            <h3>{t("termsAndConditions.section7.earlyReturn.title")}</h3>
            <p>{t("termsAndConditions.section7.earlyReturn.content")}</p>

            <h3>{t("termsAndConditions.section7.vehicleUnavailability.title")}</h3>
            <p>{t("termsAndConditions.section7.vehicleUnavailability.content")}</p>

            <h3>{t("termsAndConditions.section7.damageAssessment.title")}</h3>
            <p>{t("termsAndConditions.section7.damageAssessment.content")}</p>
          </section>

          {/* Section 8: RENTER Responsibilities */}
          <section className="privacy-section">
            <h2>{t("termsAndConditions.section8.title")}</h2>

            <h3>{t("termsAndConditions.section8.responsibleFor.title")}</h3>
            {renderList(t("termsAndConditions.section8.responsibleFor.items", { returnObjects: true }))}
            <p><strong>Towing situations include:</strong></p>
            {renderList(t("termsAndConditions.section8.responsibleFor.towingSituations", { returnObjects: true }))}

            <h3>{t("termsAndConditions.section8.additionalCharges.title")}</h3>
            <p>{t("termsAndConditions.section8.additionalCharges.content")}</p>

            <h3>{t("termsAndConditions.section8.crossBorderTravel.title")}</h3>
            <p>{t("termsAndConditions.section8.crossBorderTravel.content")}</p>
          </section>

          {/* Section 9: Prohibited Uses */}
          <section className="privacy-section">
            <h2>{t("termsAndConditions.section9.title")}</h2>

            <h3>{t("termsAndConditions.section9.prohibitedList.title")}</h3>
            {renderList(t("termsAndConditions.section9.prohibitedList.items", { returnObjects: true }))}

            <h3>{t("termsAndConditions.section9.crossBorderNotification.title")}</h3>
            <p>{t("termsAndConditions.section9.crossBorderNotification.content")}</p>

            <h3>{t("termsAndConditions.section9.mechanicalIssues.title")}</h3>
            <p>{t("termsAndConditions.section9.mechanicalIssues.content")}</p>

            <h3>{t("termsAndConditions.section9.repairRefusal.title")}</h3>
            <p>{t("termsAndConditions.section9.repairRefusal.content")}</p>
            <p>{t("termsAndConditions.section9.repairRefusal.consequence")}</p>

            <h3>{t("termsAndConditions.section9.gasBottle.title")}</h3>
            <p>{t("termsAndConditions.section9.gasBottle.content")}</p>
            <p>{t("termsAndConditions.section9.gasBottle.reimbursement")}</p>
            <p>{t("termsAndConditions.section9.gasBottle.invoiceRequirement")}</p>
            <p>{t("termsAndConditions.section9.gasBottle.noInvoice")}</p>

            <h3>{t("termsAndConditions.section9.gpsTracking.title")}</h3>
            <p>{t("termsAndConditions.section9.gpsTracking.content")}</p>
          </section>

          {/* Section 10: Protection Packages */}
          <section className="privacy-section">
            <h2>{t("termsAndConditions.section10.title")}</h2>

            <h3>{t("termsAndConditions.section10.renterLiability.title")}</h3>
            <p>{t("termsAndConditions.section10.renterLiability.content")}</p>

            <h3>{t("termsAndConditions.section10.cdwLimitations.title")}</h3>
            <p>{t("termsAndConditions.section10.cdwLimitations.content")}</p>

            <h3>{t("termsAndConditions.section10.additionalDeposit.title")}</h3>
            <p>{t("termsAndConditions.section10.additionalDeposit.content")}</p>

            <h4>{t("termsAndConditions.section10.packageDetails.title")}</h4>

            {/* Basic carries its exclusions as a list, the other two as a
                sentence, so only Basic renders the heading plus a list. */}
            <div className="protection-package">
              <h5>{t("termsAndConditions.section10.packageDetails.basic.title")}</h5>
              <p>{t("termsAndConditions.section10.packageDetails.basic.liability")}</p>
              <p><em>{t("termsAndConditions.section10.packageDetails.doesNotInclude")}</em></p>
              {renderList(t("termsAndConditions.section10.packageDetails.basic.exclusions", { returnObjects: true }))}
            </div>

            {["premium", "ultimate"].map((tier) => (
              <div className="protection-package" key={tier}>
                <h5>{t(`termsAndConditions.section10.packageDetails.${tier}.title`)}</h5>
                <p>{t(`termsAndConditions.section10.packageDetails.${tier}.liability`)}</p>
                <p><em>{t(`termsAndConditions.section10.packageDetails.${tier}.exclusions`)}</em></p>
              </div>
            ))}

            <h4>{t("termsAndConditions.section10.packageDetails.notCovered.title")}</h4>
            {renderList(t("termsAndConditions.section10.packageDetails.notCovered.items", { returnObjects: true }))}

            <h3>{t("termsAndConditions.section10.depositRetention.title")}</h3>
            <p>{t("termsAndConditions.section10.depositRetention.content")}</p>

            <h3>{t("termsAndConditions.section10.excessRepairCosts.title")}</h3>
            <p>{t("termsAndConditions.section10.excessRepairCosts.content")}</p>

            <h3>{t("termsAndConditions.section10.damageRepairDiscretion.title")}</h3>
            <p>{t("termsAndConditions.section10.damageRepairDiscretion.content")}</p>
          </section>

          {/* Section 11: Damage Policy */}
          <section className="privacy-section">
            <h2>{t("termsAndConditions.section11.title")}</h2>
            <p>{t("termsAndConditions.section11.intro")}</p>

            <h3>{t("termsAndConditions.section11.renterResponsibility.title")}</h3>
            <p>{t("termsAndConditions.section11.renterResponsibility.content")}</p>
            <p>{t("termsAndConditions.section11.renterResponsibility.policyClarity")}</p>
            <p>{t("termsAndConditions.section11.renterResponsibility.protectionNote")}</p>
            <p>{t("termsAndConditions.section11.renterResponsibility.reservesRight")}</p>
            {renderList(t("termsAndConditions.section11.renterResponsibility.includes", { returnObjects: true }))}
            <p>{t("termsAndConditions.section11.renterResponsibility.discretion")}</p>

            <h3>{t("termsAndConditions.section11.damagesAtReturn.title")}</h3>
            <p>{t("termsAndConditions.section11.damagesAtReturn.content")}</p>
            <p>{t("termsAndConditions.section11.damagesAtReturn.invoicing")}</p>
            <p><strong>Minor damages:</strong> {t("termsAndConditions.section11.damagesAtReturn.minorDamages")}</p>
            <p><strong>Substantial damages:</strong> {t("termsAndConditions.section11.damagesAtReturn.substantialDamages")}</p>

            <h3>{t("termsAndConditions.section11.damagesAfterReturn.title")}</h3>
            <p>{t("termsAndConditions.section11.damagesAfterReturn.description")}</p>
            {renderList(t("termsAndConditions.section11.damagesAfterReturn.documents", { returnObjects: true }))}
          </section>

          {/* Section 12: Mechanical Breakdown */}
          <section className="privacy-section">
            <h2>{t("termsAndConditions.section12.title")}</h2>

            <h3>{t("termsAndConditions.section12.inEvent.title")}</h3>
            {renderList(t("termsAndConditions.section12.inEvent.items", { returnObjects: true }))}

            <h3>{t("termsAndConditions.section12.knownDefects.title")}</h3>
            <p>{t("termsAndConditions.section12.knownDefects.content")}</p>

            <h3>{t("termsAndConditions.section12.repairCosts.title")}</h3>
            <p>{t("termsAndConditions.section12.repairCosts.content")}</p>

            <h3>{t("termsAndConditions.section12.replacementVehicle.title")}</h3>
            <p>{t("termsAndConditions.section12.replacementVehicle.content")}</p>
            <p>{t("termsAndConditions.section12.replacementVehicle.refund")}</p>

            <h3>{t("termsAndConditions.section12.waitingTime.title")}</h3>
            <p>{t("termsAndConditions.section12.waitingTime.content")}</p>
          </section>

          {/* Section 13: Accident Procedures */}
          <section className="privacy-section">
            <h2>{t("termsAndConditions.section13.title")}</h2>

            <h3>{t("termsAndConditions.section13.inEvent.title")}</h3>
            {renderList(t("termsAndConditions.section13.inEvent.items", { returnObjects: true }))}
          </section>

          {/* Section 14: Negligence */}
          <section className="privacy-section">
            <h2>{t("termsAndConditions.section14.title")}</h2>

            <h3>{t("termsAndConditions.section14.driverNegligence.title")}</h3>
            <p>{t("termsAndConditions.section14.driverNegligence.content")}</p>
            {renderList(t("termsAndConditions.section14.driverNegligence.items", { returnObjects: true }))}
            <p>{t("termsAndConditions.section14.driverNegligence.assessment")}</p>

            <h3>{t("termsAndConditions.section14.damageAssessment.title")}</h3>
            <p>{t("termsAndConditions.section14.damageAssessment.content")}</p>

            <h3>{t("termsAndConditions.section14.fuelInWaterTank.title")}</h3>
            <p>{t("termsAndConditions.section14.fuelInWaterTank.content")}</p>

            <h3>{t("termsAndConditions.section14.awningDamage.title")}</h3>
            <p>{t("termsAndConditions.section14.awningDamage.content")}</p>

            <h3>{t("termsAndConditions.section14.replacementVehicle.title")}</h3>
            <p>{t("termsAndConditions.section14.replacementVehicle.content")}</p>

            <h3>{t("termsAndConditions.section14.breakdownAssessment.title")}</h3>
            <p>{t("termsAndConditions.section14.breakdownAssessment.content")}</p>

            <h3>{t("termsAndConditions.section14.lostKeys.title")}</h3>
            <p>{t("termsAndConditions.section14.lostKeys.content")}</p>

            <h3>{t("termsAndConditions.section14.warningLights.title")}</h3>
            <p>{t("termsAndConditions.section14.warningLights.content")}</p>

            <h3>{t("termsAndConditions.section14.drivingWithWarningLights.title")}</h3>
            <p>{t("termsAndConditions.section14.drivingWithWarningLights.content")}</p>
          </section>

          {/* Section 15: Contract Termination */}
          <section className="privacy-section">
            <h2>{t("termsAndConditions.section15.title")}</h2>
            <p>{t("termsAndConditions.section15.content")}</p>
            {renderList(t("termsAndConditions.section15.reasons", { returnObjects: true }))}
          </section>

          {/* Section 16: Privacy */}
          <section className="privacy-section">
            <h2>{t("termsAndConditions.section16.title")}</h2>
            <p>{t("termsAndConditions.section16.content")}</p>
            <p>{t("termsAndConditions.section16.dataSharing")}</p>
            <p>{t("termsAndConditions.section16.retention")}</p>
            <p>{t("termsAndConditions.section16.optOut")}</p>
          </section>

          {/* Section 17: Complaints and Dispute Resolution */}
          <section className="privacy-section">
            <h2>{t("termsAndConditions.section17.title")}</h2>
            <p>{t("termsAndConditions.section17.content")}</p>
            <p>{t("termsAndConditions.section17.complaintsBook")}</p>
            <p>{t("termsAndConditions.section17.arbitration")}</p>
            <p>{t("termsAndConditions.section17.law")}</p>
          </section>

          {/* Contact Information */}
          <section className="privacy-section">
            <h2>{t("contact.title")}</h2>
            <div className="contact-info">
              <p className="contact-info-title">{t("contact.companyName")}</p>
              <div className="contact-info-grid">
                <p className="contact-info-item">
                  <span className="label">Company Name:</span> Bússola Poderosa
                </p>
                <p className="contact-info-item">
                  <span className="label">Tax ID (NIF):</span> 517459507
                </p>
                <p className="contact-info-item full-width">
                  <span className="label">Address:</span> Av. Pedro Álvares Cabral 147, Linhó, 2710-144 Sintra
                </p>
                <p className="contact-info-item">
                  <span className="label">Email:</span> info@suncatchers.pt
                </p>
                <p className="contact-info-item">
                  <span className="label">Phone:</span> +351 925 025 908{" "}
                  <span className="contact-phone-note">({t("contact.phoneNote")})</span>
                </p>
                <p className="contact-info-item">
                  <span className="label">Website:</span> suncatchers.pt
                </p>
                <p className="contact-info-item">
                  <span className="label">Emergency:</span> +351 925 025 908{" "}
                  <span className="contact-phone-note">({t("contact.phoneNote")})</span>
                </p>
              </div>
            </div>
          </section>

          <p className="governing-law"><em>{t("termsAndConditions.governingLaw")}</em></p>
        </div>
      </div>
      <CheckoutFooter />
    </div>
  );
};

export default TermsAndConditions;
